import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Form, Input, InputNumber, Modal, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import { productSchema as yupSchema } from "@lib/schemas/product";
import { createYupSync } from "@lib/helpers/TableHelper";
import { Product, WalletAddressType } from "@lib/types/Billing";
import { FormattedMessage } from "react-intl";
import { walletAddressTypeImage } from "@lib/helpers/ColorHelper";
import { convertNumericFields } from "@lib/types/General";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props<T> = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<T>;
  product?: T;
};

export default function ProductModal<T extends Product>({
  openModal,
  toggleModal,
  mutationQuery,
  product,
}: Props<T>) {
  const { messages } = useIntl();
  const [form] = Form.useForm();

  const { mutate, isPending } = mutationQuery;

  const initialValues = useMemo(() => {
    return product
      ? convertNumericFields(product)
      : ({
          name: "",
          description: "",
          currency: WalletAddressType.USDT,
          unitPrice: 0,
        } as Partial<T>);
  }, [product]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal]);

  useEffect(() => {
    if (!openModal) return;

    handleResetForm();
  }, [openModal]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={product ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.product"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<T>
        {...layout}
        form={form}
        name={"product-modal"}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={{ enabled: true, ...product }}
        onFinish={(values) => mutate({ ...values, id: product?.id })}
      >
        <Form.Item
          name="name"
          label={messages["common.productName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={messages["common.description"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          name="unitPrice"
          label={messages["common.unitPrice"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <InputNumber
            style={{ width: "100%" }}
            addonAfter={
              <StyledFlex style={{ gap: 5, width: "70px" }}>
                <img
                  src={walletAddressTypeImage(WalletAddressType.USDT)}
                  style={{ maxHeight: "15px" }}
                />
                <Typography>{WalletAddressType.USDT}</Typography>
              </StyledFlex>
            }
          />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            <ActionButton
              type="reset"
              onClick={handleResetForm}
              loading={isPending}
            />

            <ActionButton
              type={!!product ? "save" : "add"}
              loading={isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
